import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { Loader2, CheckCircle2, Shield, RotateCcw } from 'lucide-react'
import IdentityEndpoints from '../../endpoints/IdentityEndpoints'
import { Toaster, toast } from 'sonner'

const TERMS = `By checking this box, I understand that my photo ID is:

\u2022 To be used solely by whom it was issued.

\u2022 Non-refundable, non-transferable & non-upgradable.

\u2022 Subject to a $34.00 reprint fee if lost or stolen.

\u2022 Provides no in-and-out privileges to the grounds. A valid hand stamp is required for re-entry each day.`

const TERMS_VERSION = '2026.1'

export const CneAccreditation: React.FC = () => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = (): boolean => {
    const e: Record<string, string> = {}
    if (!firstName.trim()) e.firstName = 'First name is required'
    if (!lastName.trim()) e.lastName = 'Last name is required'
    if (!email.trim()) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Please enter a valid email address'
    if (!companyName.trim()) e.companyName = 'Company/booth name is required'
    if (!acceptedTerms) e.terms = 'You must agree to the terms'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  // The form runs on a locked-down kiosk iPad, so the confirmation screen has to
  // hand a blank form back to the next person without reloading the page.
  const resetForm = () => {
    setFirstName('')
    setLastName('')
    setEmail('')
    setCompanyName('')
    setAcceptedTerms(false)
    setErrors({})
    setLoading(false)
    setDone(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setLoading(true)
    try {
      await IdentityEndpoints.createPendingIdentity({
        clientId: 3,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        companyName: companyName.trim(),
        acceptedTerms: true,
        termsVersion: TERMS_VERSION,
      })
      setDone(true)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'An error occurred'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-10 pb-10 space-y-4">
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-xl font-semibold">Thank You!</h2>
            <p className="text-muted-foreground">
              Your registration has been received and your agreement to the
              terms &amp; conditions has been recorded. Please visit the Photo
              ID station to complete your accreditation.
            </p>
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={resetForm}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Register Another Person
            </Button>
          </CardContent>
        </Card>
        <Toaster position="top-right" richColors />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="flex justify-center">
            <img
              src="https://manage.authentigate.ca/uploads/70/CNE-LOGO_FINAL-with-WHT-KEYLINE.png"
              alt="CNE Logo"
              className="w-[220px] max-w-[80%] drop-shadow-md"
            />
          </div>
          <div>
            <CardTitle className="text-xl">Accreditation Registration</CardTitle>
            <CardDescription className="mt-1">
              All vendors and vendor staff must complete this form each year to
              agree to the terms &amp; conditions — even if you already have a
              photo ID with us.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  First Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); if (errors.firstName) setErrors((p) => ({ ...p, firstName: '' })) }}
                  disabled={loading}
                  className={errors.firstName ? 'border-red-500' : ''}
                />
                {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">
                  Last Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => { setLastName(e.target.value); if (errors.lastName) setErrors((p) => ({ ...p, lastName: '' })) }}
                  disabled={loading}
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && <p className="text-xs text-red-500">{errors.lastName}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: '' })) }}
                disabled={loading}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName">
                Company / Booth Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => { setCompanyName(e.target.value); if (errors.companyName) setErrors((p) => ({ ...p, companyName: '' })) }}
                disabled={loading}
                className={errors.companyName ? 'border-red-500' : ''}
              />
              {errors.companyName && <p className="text-xs text-red-500">{errors.companyName}</p>}
            </div>

            {/* Terms */}
            <div className="space-y-3 pt-2">
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Terms & Conditions</span>
                </div>
                <p className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed">
                  {TERMS}
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => {
                    setAcceptedTerms(checked === true)
                    if (errors.terms) setErrors((p) => ({ ...p, terms: '' }))
                  }}
                  disabled={loading}
                  className={errors.terms ? 'border-red-500' : ''}
                />
                <label
                  htmlFor="terms"
                  className="text-sm leading-tight cursor-pointer"
                >
                  <span className="text-destructive">* </span>
                  I agree with the terms and conditions above
                </label>
              </div>
              {errors.terms && <p className="text-xs text-red-500">{errors.terms}</p>}
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Registration'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Toaster position="top-right" richColors />
    </div>
  )
}
